import axios, { axiosInstanceNoInterceptors } from './axiosInstance';
import { APIUser, APIUserInfo } from '../../types';

interface LoginParams {
  userName?: string;
  email?: string;
  password?: string;
  magicAuthToken?: string;
}

interface MfaRequiredResponse {
  mfaRequired: true;
  authenticationSessionId: string;
  authenticationSessionToken: string;
}

type LoginResponse = APIUserInfo | MfaRequiredResponse;

interface SSODomainLookupResponse {
  ssoEnabled: boolean;
  ssoProvider?: string;
  ssoUrl?: string;
}

interface ValidateTotpParams {
  totp: string;
  authenticationSessionId: string;
  authenticationSessionToken: string;
}

interface RegisterParams {
  userName: string;
  password: string;
  email: string;
  name: string;
  agreedToTermsAndPrivacy: boolean;
  captchaToken?: string;
  emailVerificationToken?: string;
}

const login = ({
  userName,
  email,
  password,
  magicAuthToken,
}: LoginParams): Promise<LoginResponse> => {
  return axios
    .post('/authenticate', {
      userName,
      email,
      password,
      magicAuthToken,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const checkEmailForSSO = (
  email: string,
  clientRedirectUrl?: string,
): Promise<SSODomainLookupResponse> => {
  return axios
    .post('/auth/sso/domain-lookup', {
      email,
      // Optional round-trip state: the SSO callback appends ?magicAuthToken=
      // to this URL, so a /login?continue=<ref> value carries the OAuth
      // consent flow through the Cognito/SAML leg (spec §6.2).
      clientRedirectUrl,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const validateTotp = ({
  totp,
  authenticationSessionId,
  authenticationSessionToken,
}: ValidateTotpParams): Promise<APIUserInfo> => {
  return axios
    .post('/authenticate/mfa/totp/validate', {
      totp,
      authenticationSessionId,
      authenticationSessionToken,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const forgotPassword = (email: string): Promise<void> => {
  return axios
    .post('/user/forgotpassword', {
      email: email ? email.trim() : email,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const updatePassword = (oldPassword: string, newPassword: string): Promise<void> => {
  return axios
    .post('/user', {
      password: oldPassword,
      newPassword: newPassword,
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

/**
 * Sign out. POST /authenticate/logout (ULLR-189) revokes this session's token
 * server-side and clears the HttpOnly `ullr` cookie (JS cannot clear it). If
 * that call fails, GET /logout is tried: it only clears the cookie (it revokes
 * nothing), which still signs this browser out.
 * Never rejects. Resolves false only when BOTH calls failed: the cookie may
 * still be set, so the caller must not pretend the user is signed out (see
 * performLogout in login/authUtils).
 */
const logout = (): Promise<boolean> => {
  return axiosInstanceNoInterceptors
    .post('/authenticate/logout', {})
    .then(() => true)
    .catch((error) => {
      console.log(error);
      return axiosInstanceNoInterceptors
        .get('/logout')
        .then(() => true)
        .catch((fallbackError) => {
          console.log(fallbackError);
          return false;
        });
    });
};

const register = ({
  userName,
  password,
  email,
  name,
  agreedToTermsAndPrivacy,
  captchaToken,
  emailVerificationToken,
}: RegisterParams): Promise<APIUserInfo | null> => {
  return axios
    .post(
      '/register',
      {
        userName,
        password,
        email,
        name,
        agreedToTermsAndPrivacy,
        emailVerificationToken,
      },
      {
        headers: {
          'g-recaptcha-response': captchaToken || '',
        },
      }
    )
    .then((response) => {
      return response?.data;
    })
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

export interface VerifyEmailErrorResponse {
  message: string;
  requestId: string;
  remediation: 'retry' | 'contact_support';
}

const verifyEmail = (token: string): Promise<{ success: true }> => {
  // Bypass the shared 401 interceptor: /verifyemail is a public page and the
  // caller handles errors by routing to /verifyemailerror.
  return axiosInstanceNoInterceptors
    .post('/user/verifyemail', { token })
    .then((response) => response.data)
    .catch((error) => {
      console.log(error);
      throw error;
    });
};

const getUserUserName = (userName: string): Promise<APIUser | null> => {
  return axios
    .get(`/user?un=${encodeURIComponent(userName)}`)
    .then((response) => {
      return response?.data;
    })
    .catch((error) => {
      console.log(error);
      return null;
    });
};

interface AuthenticateHandoffResponse {
  magicToken: string;
}

/**
 * OAuth login-bridge handoff (spec docs/specs/mcp-server §6.2): mint a
 * single-use magic auth token for the CALLER'S OWN session. Called from the
 * consent view's Allow; the token is forwarded to the authorization server's
 * /oauth/callback, which redeems it server-side.
 */
const authenticateHandoff = (): Promise<AuthenticateHandoffResponse> => {
  return axios
    .post('/authenticate/handoff')
    .then((response) => {
      return response.data;
    });
};

const getUserEmail = (email: string): Promise<APIUser | null> => {
  return axios
    .get(`/user?email=${encodeURIComponent(email)}`)
    .then((response) => {
      return response?.data;
    })
    .catch((error) => {
      console.log(error);
      return null;
    });
};

const authFunctions = {
  login,
  checkEmailForSSO,
  authenticateHandoff,
  validateTotp,
  logout,
  forgotPassword,
  updatePassword,
  register,
  verifyEmail,
  getUserUserName,
  getUserEmail,
};

export default authFunctions;
